import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line
} from 'recharts';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from '../../utils/translations';
import { format, formatDistanceToNow } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import supabase from '../../lib/supabase';
import Button from "../Common/Button"
import { toast } from "sonner"

const { FiMessageSquare, FiUsers, FiTrendingUp, FiActivity, FiFilter, FiEye, FiMoreHorizontal, FiCalendar, FiSearch, FiX, FiClock, FiAlertCircle } = FiIcons;

const COLORS = ['#007ee5', '#10b981', '#8b5cf6', '#f59e0b', '#ef4444'];

const StatisticsPage = ({ setActiveTab }) => {
  const { user, teams } = useAuth();
  const t = useTranslation(user?.language || 'fr'); // Default to French
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [agentFilter, setAgentFilter] = useState([]);
  const [userQuery, setUserQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [totalMessages, setTotalMessages] = useState(0);
  const [teamMessages, setTeamMessages] = useState(0); // Initialize as 0 instead of null
  const [dailyMessages, setDailyMessages] = useState([]);
  const [topAgents, setTopAgents] = useState([]);
  const [recentChats, setRecentChats] = useState([]);
  const [latestQuestions, setLatestQuestions] = useState([]);
  const [latestQuestionsLoading, setLatestQuestionsLoading] = useState(false);
  const [latestQuestionsError, setLatestQuestionsError] = useState('');
  const [availableAgents, setAvailableAgents] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [searchTeam, setSearchTeam] = useState('');

  const monthOptions = useMemo(() => {
    const options = [];
    const now = new Date();
    for (let i = 0; i < 6; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      options.push({
        value: date,
        label: format(date, 'MMMM yyyy', { locale: user?.language === 'fr' ? fr : enUS })
      });
    }
    return options;
  }, [user?.language]);

  useEffect(() => {
    const fetchAgents = async () => {
      try {
        let agents = [];
        if (searchTeam) {
          const { data: allowedAgentIds, error: teamError } = await supabase
            .from('team_allowed_agents_a1b2c3d4e5')
            .select('agent_id')
            .eq('team_id', searchTeam.id);
          if (teamError) throw teamError;

          const agentIds = allowedAgentIds ? allowedAgentIds.map(a => a.agent_id) : [];
          if (agentIds.length > 0) {
            const { data: agentData, error: agentError } = await supabase
              .from('ai_agents_a1b2c3d4e5')
              .select('id, name')
              .in('id', agentIds)
              .order('name');
            if (agentError) throw agentError;
            agents = agentData || [];
          }
        } else {
          const { data: allAgents, error } = await supabase
            .from('ai_agents_a1b2c3d4e5')
            .select('id, name')
            .order('name');
          if (error) throw error;
          agents = allAgents || [];
        }

        setAvailableAgents(agents);
        setAgentFilter(prev => prev.filter(id => agents.some(agent => agent.id === id)));
      } catch (error) {
        console.error('Error fetching agents:', error);
        setAvailableAgents([]);
      }
    };

    fetchAgents();
  }, [searchTeam]);

  const fetchLatestQuestions = useCallback(async () => {
    if (!user) return;
    setLatestQuestionsLoading(true);
    setLatestQuestionsError('');

    try {
      const year = selectedMonth.getFullYear();
      const month = selectedMonth.getMonth();
      const start = new Date(year, month, 1).toISOString();
      const end = new Date(year, month + 1, 0, 23, 59, 59, 999).toISOString();

      let query = supabase
        .from('messages_a1b2c3d4e5')
        .select('id, content, created_at, user_id, agent_id')
        .gte('created_at', start)
        .lte('created_at', end)
        .order('created_at', { ascending: false })
        .limit(10);

      if (agentFilter.length > 0) {
        query = query.in('agent_id', agentFilter);
      }

      if (searchTeam) {
        const { data: teamUsers, error: teamError } = await supabase
          .from('team_members_a1b2c3d4e5')
          .select('user_id')
          .eq('team_id', searchTeam.id);
        if (teamError) throw teamError;
        const teamUserIds = teamUsers ? teamUsers.map(u => u.user_id) : [];
        if (teamUserIds.length > 0) {
          query = query.in('user_id', teamUserIds);
        }
      }

      const { data, error } = await query;
      if (error) throw error;

      if (data && data.length > 0) {
        const userIds = [...new Set(data.map(msg => msg.user_id))];
        const { data: userData, error: userError } = await supabase
          .from('user_profiles_a1b2c3d4e5')
          .select('id, full_name')
          .in('id', userIds);

        if (userError) throw userError;

        const userMap = {};
        if (userData) {
          userData.forEach(user => {
            userMap[user.id] = user.full_name;
          });
        }

        const processedQuestions = data.map(msg => ({
          id: msg.id,
          content: msg.content,
          userName: userMap[msg.user_id] || t('statistics.unknownUser', 'Unknown User'),
          created_at: msg.created_at,
          timestamp: formatDistanceToNow(new Date(msg.created_at), { 
            addSuffix: true, 
            locale: user?.language === 'fr' ? fr : enUS 
          })
        }));

        setLatestQuestions(processedQuestions);
      } else {
        setLatestQuestions([]);
      }
    } catch (error) {
      console.error('Error fetching latest questions:', error);
      setLatestQuestionsError(t('statistics.latestQuestionsError', 'Failed to load latest questions'));
    } finally {
      setLatestQuestionsLoading(false);
    }
  }, [user, selectedMonth, agentFilter, searchTeam]);

  const fetchAnalyticsData = useCallback(async () => {
    if (!user) return;
    setLoading(true);

    try {
      const year = selectedMonth.getFullYear();
      const month = selectedMonth.getMonth();
      const start = new Date(year, month, 1).toISOString();
      const end = new Date(year, month + 1, 0, 23, 59, 59, 999).toISOString();

      // Fetch all team IDs for the user if no specific team is selected
      let teamUserIds = [];
      if (searchTeam) {
        const { data: teamUsers, error: teamError } = await supabase
          .from('team_members_a1b2c3d4e5')
          .select('user_id')
          .eq('team_id', searchTeam.id);
        if (teamError) throw teamError;
        teamUserIds = teamUsers ? teamUsers.map(u => u.user_id) : [];
      } else {
        // Fetch all teams the user is part of
        const { data: userTeams, error: userTeamsError } = await supabase
          .from('team_members_a1b2c3d4e5')
          .select('team_id')
          .eq('user_id', user.id);
        if (userTeamsError) throw userTeamsError;
        const teamIds = userTeams ? userTeams.map(t => t.team_id) : [];
        if (teamIds.length > 0) {
          const { data: teamUsers, error: teamError } = await supabase
            .from('team_members_a1b2c3d4e5')
            .select('user_id')
            .in('team_id', teamIds);
          if (teamError) throw teamError;
          teamUserIds = teamUsers ? teamUsers.map(u => u.user_id) : [];
        }
      }

      // Total messages query
      let baseQuery = supabase
        .from('messages_a1b2c3d4e5')
        .select('id', { count: 'exact', head: true })
        .gte('created_at', start)
        .lte('created_at', end);

      if (agentFilter.length > 0) {
        baseQuery = baseQuery.in('agent_id', agentFilter);
      }

      if (teamUserIds.length > 0) {
        baseQuery = baseQuery.in('user_id', teamUserIds);
      }

      const { count: totalCount, error: totalError } = await baseQuery;
      if (totalError) throw totalError;
      setTotalMessages(totalCount ?? 0);

      // Team messages query
      let teamQuery = supabase
        .from('messages_a1b2c3d4e5')
        .select('id', { count: 'exact', head: true })
        .gte('created_at', start)
        .lte('created_at', end);

      if (agentFilter.length > 0) {
        teamQuery = teamQuery.in('agent_id', agentFilter);
      }

      if (teamUserIds.length > 0) {
        teamQuery = teamQuery.in('user_id', teamUserIds);
      }

      const { count: teamMsgCount, error: teamError } = await teamQuery;
      if (teamError) throw teamError;
      setTeamMessages(teamMsgCount ?? 0);

      // Daily messages query
      let dailyQuery = supabase
        .from('messages_a1b2c3d4e5')
        .select('created_at')
        .gte('created_at', start)
        .lte('created_at', end);

      if (agentFilter.length > 0) {
        dailyQuery = dailyQuery.in('agent_id', agentFilter);
      }

      if (teamUserIds.length > 0) {
        dailyQuery = dailyQuery.in('user_id', teamUserIds);
      }

      const { data: dailyRows, error: dailyError } = await dailyQuery;
      if (dailyError) throw dailyError;

      const dailyMap = Object.create(null);
      if (dailyRows) {
        dailyRows.forEach(row => {
          const d = new Date(row.created_at);
          const key = [
            d.getFullYear(),
            String(d.getMonth() + 1).padStart(2, '0'),
            String(d.getDate()).padStart(2, '0'),
          ].join('-');
          dailyMap[key] = (dailyMap[key] || 0) + 1;
        });
      }

      const dailyData = Object.entries(dailyMap)
        .map(([day, count]) => ({ day, count }))
        .sort((a, b) => a.day.localeCompare(b.day));
      setDailyMessages(dailyData);

      // Top agents query
      let agentQuery = supabase
        .from('messages_a1b2c3d4e5')
        .select('agent_id')
        .gte('created_at', start)
        .lte('created_at', end);

      if (agentFilter.length > 0) {
        agentQuery = agentQuery.in('agent_id', agentFilter);
      }

      if (teamUserIds.length > 0) {
        agentQuery = agentQuery.in('user_id', teamUserIds);
      }

      const { data: agentRows, error: agentError } = await agentQuery;
      if (agentError) throw agentError;

      const agentCounts = Object.create(null);
      if (agentRows) {
        agentRows.forEach(row => {
          agentCounts[row.agent_id] = (agentCounts[row.agent_id] || 0) + 1;
        });
      }

      const { data: agents } = await supabase
        .from('ai_agents_a1b2c3d4e5')
        .select('id,name');

      const agentMap = Object.create(null);
      if (agents) {
        agents.forEach(agent => {
          agentMap[agent.id] = agent.name;
        });
      }

      const totalAgentMessages = Object.values(agentCounts).reduce((sum, count) => sum + count, 0);
      const topAgentsList = Object.entries(agentCounts)
        .map(([agentId, count]) => ({
          id: agentId,
          name: agentMap[agentId] || t('statistics.unknownAgent', 'Agent {id}').replace('{id}', agentId),
          count,
          percentage: totalAgentMessages > 0 ? Math.round((count / totalAgentMessages) * 100) : 0
        }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 3);
      setTopAgents(topAgentsList);

      // Recent chats query
      let recentQuery = supabase
        .from('messages_a1b2c3d4e5')
        .select('user_id,agent_id,content,created_at')
        .gte('created_at', start)
        .lte('created_at', end)
        .order('created_at', { ascending: false })
        .limit(20);

      if (agentFilter.length > 0) {
        recentQuery = recentQuery.in('agent_id', agentFilter);
      }

      if (teamUserIds.length > 0) {
        recentQuery = recentQuery.in('user_id', teamUserIds);
      }

      const { data: recentMsgs, error: recentError } = await recentQuery;
      if (recentError) throw recentError;

      const userIds = [...new Set((recentMsgs || []).map(msg => msg.user_id))];
      const agentIds = [...new Set((recentMsgs || []).map(msg => msg.agent_id))];

      const [usersResult, agentsResult] = await Promise.all([
        userIds.length > 0 ? supabase
          .from('user_profiles_a1b2c3d4e5')
          .select('id,full_name')
          .in('id', userIds) : { data: [] },
        agentIds.length > 0 ? supabase
          .from('ai_agents_a1b2c3d4e5')
          .select('id,name')
          .in('id', agentIds) : { data: [] }
      ]);

      const userMap = Object.create(null);
      const agentMapForRecent = Object.create(null);

      if (usersResult.data) {
        usersResult.data.forEach(user => {
          userMap[user.id] = user.full_name;
        });
      }

      if (agentsResult.data) {
        agentsResult.data.forEach(agent => {
          agentMapForRecent[agent.id] = agent.name;
        });
      }

      let filteredRecentMsgs = (recentMsgs || []);
      if (userQuery.trim()) {
        const query = userQuery.toLowerCase();
        filteredRecentMsgs = filteredRecentMsgs.filter(msg => {
          const userName = userMap[msg.user_id] || '';
          return userName.toLowerCase().includes(query);
        });
      }

      const recentChatsData = filteredRecentMsgs.map(msg => ({
        id: `${msg.user_id}-${msg.created_at}`,
        user: userMap[msg.user_id] || t('statistics.unknownUser', 'Unknown User'),
        agent: agentMapForRecent[msg.agent_id] || t('statistics.unknownAgent', 'Agent {id}').replace('{id}', msg.agent_id),
        content: msg.content,
        time: format(new Date(msg.created_at), 'MMM d, HH:mm', { locale: user?.language === 'fr' ? fr : enUS }),
        status: t('statistics.statusCompleted', 'completed')
      }));
      setRecentChats(recentChatsData);

    } catch (error) {
      console.error('Error fetching analytics data:', error);
      toast.error(t('statistics.analyticsError', 'Failed to load analytics data'));
    } finally {
      setLoading(false);
    }
  }, [user, selectedMonth, agentFilter, userQuery, searchTeam]);

  useEffect(() => {
    if (user) {
      fetchLatestQuestions();
    }
  }, [user, selectedMonth, agentFilter, searchTeam, fetchLatestQuestions]);

  useEffect(() => {
    if (user) {
      fetchAnalyticsData();
    }
  }, [user, selectedMonth, agentFilter, userQuery, searchTeam, fetchAnalyticsData]);

  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel('realtime-messages')
      .on('postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages_a1b2c3d4e5'
        },
        async (payload) => {
          const ts = payload?.new?.created_at ? new Date(payload.new.created_at) : null;
          if (!ts) return;
          const selYear = selectedMonth.getFullYear();
          const selMonth = selectedMonth.getMonth();
          if (ts.getFullYear() === selYear && ts.getMonth() === selMonth) {
            let shouldRefresh = true;
            if (searchTeam) {
              const { data: userProfile, error } = await supabase
                .from('team_members_a1b2c3d4e5')
                .select('team_id')
                .eq('user_id', payload.new.user_id)
                .single();
              if (error || userProfile?.team_id !== searchTeam.id) {
                shouldRefresh = false;
              }
            } else {
              // Check if the user is in any of the user's teams
              const { data: userTeams, error } = await supabase
                .from('team_members_a1b2c3d4e5')
                .select('team_id')
                .eq('user_id', payload.new.user_id);
              if (error || !userTeams.some(t => teams.map(team => team.id).includes(t.team_id))) {
                shouldRefresh = false;
              }
            }
            if (shouldRefresh) {
              clearTimeout(window.__analyticsDebounce);
              window.__analyticsDebounce = setTimeout(() => {
                fetchAnalyticsData();
                fetchLatestQuestions();
              }, 250);
            }
          }
        })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, selectedMonth, searchTeam, fetchAnalyticsData, fetchLatestQuestions, teams]);

  const handleAgentFilterChange = (agentId) => {
    setAgentFilter(prev =>
      prev.includes(agentId)
        ? prev.filter(id => id !== agentId)
        : [...prev, agentId]
    );
  };

  const clearFilters = () => {
    setAgentFilter([]);
    setUserQuery('');
  };

  if (loading && totalMessages === 0) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">{t('statistics.loadingAnalytics', 'Loading analytics...')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto p-4 lg:p-6">
      <div className="max-w-12xl mx-auto space-y-4 lg:space-y-6">
        {/* Filter Controls */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:space-x-4">
            {/* Month Selector */}
            <div className="flex items-center space-x-2 mb-2 lg:mb-0">
              <SafeIcon icon={FiCalendar} className="w-4 lg:w-5 h-4 lg:h-5 text-gray-500" />
              <select
                value={selectedMonth.getTime()}
                onChange={(e) => setSelectedMonth(new Date(parseInt(e.target.value)))}
                className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500 dark:text-white w-full lg:w-auto"
              >
                {monthOptions.map(option => (
                  <option
                    key={option.value.getTime()}
                    value={option.value.getTime()}
                  >
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Team Selector */}
            <div className="flex items-center space-x-2">
              <SafeIcon icon={FiUsers} className="w-4 lg:w-5 h-4 lg:h-5 text-gray-500" />
              <select
                value={searchTeam.id || ''}
                onChange={(e) => setSearchTeam(e.target.value ? { id: e.target.value, name: e.target.options[e.target.selectedIndex].text } : '')}
                className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500 dark:text-white w-full lg:w-auto"
              >
                <option value="">{t('statistics.allTeams', 'All Teams')}</option>
                {teams.map(team => (
                  <option key={team.id} value={team.id}>{team.name}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex items-center gap-2 lg:gap-3">
            {/* Filters Toggle */}
            <Button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center text-sm lg:space-x-2 px-3 lg:px-4 py-2 border rounded-lg text-sm transition-colors ${
                showFilters || agentFilter.length > 0 || userQuery
                  ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700 text-blue-600 dark:text-blue-400'
                  : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-white'
              }`}
              icon={FiFilter}
              iconClassName='w-4 h-4'
            >
              <span className="hidden lg:inline">{t('statistics.filters', 'Filters')}</span>
              {(agentFilter.length > 0 || userQuery) && (
                <span className="ml-2">
                  {agentFilter.length + (userQuery ? 1 : 0)}
                </span>
              )}
            </Button>

            {(agentFilter.length > 0 || userQuery) && (
              <Button
                onClick={clearFilters}
                className="flex items-center text-sm lg:space-x-2 px-3 py-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors text-sm"
                icon={FiX}
                iconClassName='w-4 h-4'
                responsive
              >
                {t('statistics.clear', 'Clear')}
              </Button>
            )}
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-white dark:bg-gray-800 rounded-xl p-4 lg:p-6 border border-gray-200 dark:border-gray-700"
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
              {/* Agent Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('statistics.filterByAgents', 'Filter by Agents')}
                </label>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {availableAgents.map(agent => (
                    <label key={agent.id} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={agentFilter.includes(agent.id)}
                        onChange={() => handleAgentFilterChange(agent.id)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">{agent.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* User Search */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('statistics.searchUsers', 'Search Users')}
                </label>
                <div className="relative">
                  <SafeIcon
                    icon={FiSearch}
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400"
                  />
                  <input
                    type="text"
                    value={userQuery}
                    onChange={(e) => setUserQuery(e.target.value)}
                    placeholder={t('statistics.searchUsersPlaceholder', 'Search by user name...')}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-hidden focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Top Stats Cards */}
        <div className="grid grid-cols-1 gap-4 lg:gap-6">
          {/* Total Messages */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white dark:bg-gray-800 rounded-xl p-4 lg:p-6 border border-gray-200 dark:border-gray-700"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs lg:text-sm text-gray-500 dark:text-gray-400 mb-1">{t('statistics.totalMessages', 'Total Messages (Selected Month)')}</p>
                <p className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white">
                  {totalMessages.toLocaleString()}
                </p>
                <div className="flex items-center mt-2">
                  <SafeIcon icon={FiTrendingUp} className="w-3 lg:w-4 h-3 lg:h-4 text-blue-500 mr-1" />
                  <span className="text-xs lg:text-sm text-blue-600 font-medium">
                    {format(selectedMonth, 'MMMM yyyy', { locale: user?.language === 'fr' ? fr : enUS })}
                  </span>
                </div>
              </div>
              <div className="w-10 lg:w-12 h-10 lg:h-12 bg-blue-50 dark:bg-blue-900/20 rounded-xl flex items-center justify-center">
                <SafeIcon icon={FiMessageSquare} className="w-5 lg:w-6 h-5 lg:h-6 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </motion.div>

          {/* Team Messages */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white dark:bg-gray-800 rounded-xl p-4 lg:p-6 border border-gray-200 dark:border-gray-700"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs lg:text-sm text-gray-500 dark:text-gray-400 mb-1">{t('statistics.teamMessages', 'Team Messages (Selected Month)')}</p>
                <p className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white">
                  {teamMessages.toLocaleString()}
                </p>
                <div className="flex items-center mt-2">
                  <SafeIcon icon={FiUsers} className="w-3 lg:w-4 h-3 lg:h-4 text-green-500 mr-1" />
                  <span className="text-xs lg:text-sm text-green-600 font-medium">
                    {searchTeam ? searchTeam.name : t('statistics.allTeamsMessages', 'All teams messages')}
                  </span>
                </div>
              </div>
              <div className="w-10 lg:w-12 h-10 lg:h-12 bg-green-50 dark:bg-green-900/20 rounded-xl flex items-center justify-center">
                <SafeIcon icon={FiUsers} className="w-5 lg:w-6 h-5 lg:h-6 text-green-600 dark:text-green-400" />
              </div>
            </div>
          </motion.div>
        </div>

        {/* Middle Section - Charts */}
        <div className="grid grid-cols-1 gap-4 lg:gap-6">
          {/* Daily Messages Chart */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white dark:bg-gray-800 rounded-xl p-4 lg:p-6 border border-gray-200 dark:border-gray-700"
          >
            <div className="flex items-center justify-between mb-4 lg:mb-6">
              <h3 className="text-base lg:text-lg font-semibold text-gray-900 dark:text-white">{t('statistics.dailyMessages', 'Daily Messages')}</h3>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                <span className="text-xs lg:text-sm text-gray-600 dark:text-gray-400">{t('statistics.messages', 'Messages')}</span>
              </div>
            </div>
            <div className="h-[250px] lg:h-[300px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={dailyMessages}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis
                    dataKey="day"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#6b7280', fontSize: 12 }}
                    tickFormatter={(value) => {
                      const [y, m, dd] = String(value).split('-').map(Number);
                      return format(new Date(y, m - 1, dd), 'MMM d', { locale: user?.language === 'fr' ? fr : enUS });
                    }}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#6b7280', fontSize: 12 }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
                      fontSize: '12px'
                    }}
                    labelFormatter={(value) => {
                      const [y, m, dd] = String(value).split('-').map(Number);
                      return format(new Date(y, m - 1, dd), 'MMM d, yyyy', { locale: user?.language === 'fr' ? fr : enUS });
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="count"
                    stroke="#007ee5"
                    strokeWidth={2}
                    dot={{ fill: '#007ee5', strokeWidth: 2, r: 3 }}
                    activeDot={{ r: 5, stroke: '#007ee5', strokeWidth: 2 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </motion.div>

          {/* Top AI Agents */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white dark:bg-gray-800 rounded-xl p-4 lg:p-6 border border-gray-200 dark:border-gray-700"
          >
            <div className="flex items-center justify-between mb-4 lg:mb-6">
              <h3 className="text-base lg:text-lg font-semibold text-gray-900 dark:text-white">{t('statistics.topAgents', 'Top AI Agents Used')}</h3>
            </div>
            {topAgents.length > 0 ? (
              <div className="space-y-3 lg:space-y-4">
                {topAgents.map((agent, index) => (
                  <div key={agent.id} className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className={`w-6 lg:w-8 h-6 lg:h-8 rounded-lg flex items-center justify-center text-white font-medium text-sm ${index === 0 ? 'bg-blue-500' : index === 1 ? 'bg-green-500' : 'bg-purple-500'}`}>
                        {index + 1}
                      </div>
                      <div>
                        <div className="font-medium text-gray-900 dark:text-white text-sm lg:text-base">{agent.name}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{t('statistics.messagesCount', '{count} messages').replace('{count}', agent.count)}</div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-16 lg:w-20 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                        <div
                          className={`h-full transition-all duration-300 ${index === 0 ? 'bg-blue-500' : index === 1 ? 'bg-green-500' : 'bg-purple-500'}`}
                          style={{ width: `${agent.percentage}%` }}
                        />
                      </div>
                      <span className="text-xs lg:text-sm font-medium text-gray-900 dark:text-white">{agent.percentage}%</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 lg:py-8">
                <SafeIcon icon={FiActivity} className="w-10 lg:w-12 h-10 lg:h-12 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500 dark:text-gray-400 text-sm">{t('statistics.noAgentData', 'No agent data for selected period')}</p>
              </div>
            )}
          </motion.div>
        </div>

        {/* Bottom Section - Recent Chats and Latest Questions */}
        <div className="grid grid-cols-1 gap-4 lg:gap-6">
          {/* Recent Chats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700"
          >
            <div className="p-4 lg:p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-base lg:text-lg font-semibold text-gray-900 dark:text-white">{t('statistics.recentChats', 'Recent Chats')}</h3>
            </div>
            {recentChats.length > 0 ? (
              <>
                {/* Table for Desktop/Tablet */}
                <div className="overflow-x-scroll">
                  <table className="table-auto w-full">
                    <thead className="bg-gray-50 dark:bg-gray-900">
                      <tr>
                        <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('statistics.user', 'User')}</th>
                        <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('statistics.agent', 'Agent')}</th>
                        <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('statistics.time', 'Time')}</th>
                        <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('statistics.status', 'Status')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {recentChats.map((chat) => (
                        <tr key={chat.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                          <td className="px-4 lg:px-6 py-4">
                            <div className="text-sm font-medium text-gray-900 dark:text-gray-400">{chat.user}</div>
                          </td>
                          <td className="px-4 lg:px-6 py-4">
                            <div className="text-sm text-gray-600 dark:text-gray-400">{chat.agent}</div>
                          </td>
                          <td className="px-4 lg:px-6 py-4">
                            <div className="text-sm text-gray-600 dark:text-gray-400">{chat.time}</div>
                          </td>
                          <td className="px-4 lg:px-6 py-4">
                            <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800 dark:bg-gray-white dark:text-black">
                              {chat.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>                
              </>
            ) : (
              <div className="text-center py-6 lg:py-8">
                <SafeIcon icon={FiMessageSquare} className="w-10 lg:w-12 h-10 lg:h-12 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500 dark:text-gray-400 text-sm">{t('statistics.noRecentChats', 'No recent chats in selected period')}</p>
              </div>
            )}
          </motion.div>

          {/* Latest Asked Questions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-white dark:bg-gray-800 rounded-xl p-4 lg:p-6 border border-gray-200 dark:border-gray-700"
          >
            <div className="flex items-center justify-between mb-4 lg:mb-6">
              <h3 className="text-base lg:text-lg font-semibold text-gray-900 dark:text-white">{t('statistics.latestQuestions', 'Latest Asked Questions')}</h3>
            </div>
            {latestQuestionsLoading ? (
              <div className="text-center py-6 lg:py-8">
                <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                <p className="text-gray-500 dark:text-gray-400 text-sm">{t('statistics.loadingQuestions', 'Loading latest questions...')}</p>
              </div>
            ) : latestQuestionsError ? (
              <div className="text-center py-6 lg:py-8">
                <SafeIcon icon={FiAlertCircle} className="w-10 lg:w-12 h-10 lg:h-12 text-red-400 mx-auto mb-2" />
                <p className="text-red-500 dark:text-red-400 text-sm">{latestQuestionsError}</p>
              </div>
            ) : latestQuestions.length > 0 ? (
              <div className="space-y-3 lg:space-y-4">
                {latestQuestions.map((question) => (
                  <div key={question.id} className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                    <div className="flex flex-col">
                      <p 
                        className="font-medium text-gray-900 dark:text-gray-400 text-sm lg:text-base mb-2 line-clamp-2" 
                        title={question.content}
                      >
                        {question.content}
                      </p>
                      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center text-xs text-gray-600 dark:text-gray-400">
                        <span>{question.userName}</span>
                        <div className="flex items-center mt-1 lg:mt-0">
                          <SafeIcon icon={FiClock} className="w-3 h-3 mr-1" />
                          <span>{question.timestamp}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 lg:py-8">
                <SafeIcon icon={FiMessageSquare} className="w-10 lg:w-12 h-10 lg:h-12 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500 dark:text-gray-400 text-sm">{t('statistics.noRecentQuestions', 'No recent questions yet.')}</p>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default StatisticsPage;